import React, { useState } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  Button,
  Chip,
  Skeleton,
  Checkbox
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";

function ProjectTable({
  projects,
  loading,
  onEdit,
  onDelete,
  selectedIds = [],
  onSelectionChange
}) {
  const [page, setPage] = useState(1);

  const rowsPerPage = 8;

  const startIndex = (page - 1) * rowsPerPage;

  const paginatedProjects = projects.slice(
    startIndex,
    startIndex + rowsPerPage
  );

  const totalPages = Math.ceil(
    projects.length / rowsPerPage
  );

  const allSelected = projects.length > 0 && selectedIds.length === projects.length;

  const isSelected = (projectId) => selectedIds.includes(projectId);

  const toggleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange?.(projects.map((project) => project.project_id));
      return;
    }

    onSelectionChange?.([]);
  };

  const toggleSelectOne = (projectId, checked) => {
    if (checked) {
      onSelectionChange?.([...selectedIds, projectId]);
      return;
    }

    onSelectionChange?.(selectedIds.filter((id) => id !== projectId));
  };

  return (
    <Box>
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          border: "1px solid #e5e7eb",
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)"
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ background: "#f8fafc" }}>
              <TableCell
                sx={{
                  width: 56,
                  fontWeight: 600,
                  fontSize: 13,
                  color: "#475569"
                }}
              >
                <Checkbox
                  checked={allSelected}
                  indeterminate={selectedIds.length > 0 && !allSelected}
                  onChange={(event) => toggleSelectAll(event.target.checked)}
                />
              </TableCell>

              {[
                "Project Name",
                "Description",
                "Assigned To",
                "Actions"
              ].map((head) => (
                <TableCell
                  key={head}
                  sx={{
                    fontWeight: 600,
                    fontSize: 13,
                    color: "#475569"
                  }}
                >
                  {head}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && Array.from({ length: rowsPerPage }).map((_, index) => (
              <TableRow key={`project-skeleton-${index}`}>
                <TableCell>
                  <Skeleton variant="circular" width={24} height={24} />
                </TableCell>

                <TableCell>
                  <Skeleton variant="text" width={160} height={24} />
                </TableCell>

                <TableCell>
                  <Skeleton variant="text" width="88%" height={24} />
                </TableCell>

                <TableCell>
                  <Skeleton variant="rounded" width={110} height={28} />
                </TableCell>

                <TableCell>
                  <Box display="flex" gap={1}>
                    <Skeleton variant="circular" width={32} height={32} />
                    <Skeleton variant="circular" width={32} height={32} />
                  </Box>
                </TableCell>
              </TableRow>
            ))}

            {!loading && paginatedProjects.map((project) => (
              <TableRow
                key={project.project_id}
                hover
                sx={{
                  "& td": {
                    borderBottom: "1px solid #f1f5f9"
                  }
                }}
              >
                <TableCell>
                  <Checkbox
                    checked={isSelected(project.project_id)}
                    onChange={(event) => toggleSelectOne(project.project_id, event.target.checked)}
                  />
                </TableCell>

                <TableCell>
                  <Typography fontSize={14} fontWeight={600}>
                    {project.project_name}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography
                    fontSize={13}
                    color="#64748b"
                    sx={{
                      maxWidth: 320,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    {project.description || "-"}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {(project.assigned_names?.length > 0 ? project.assigned_names : ["-"]).map((name) => (
                      <Chip
                        key={name}
                        label={name}
                        size="small"
                        sx={{
                          borderRadius: 2,
                          background: "#e0f2fe",
                          color: "#0369a1",
                          fontWeight: 500
                        }}
                      />
                    ))}
                  </Box>
                </TableCell>

                <TableCell>
                  <IconButton
                    color="primary"
                    onClick={() => onEdit(project)}
                  >
                    <EditIcon />
                  </IconButton>

                  <IconButton
                    color="error"
                    onClick={() => onDelete(project)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}

            {!loading && projects.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} sx={{ py: 6 }}>
                  <Box
                    sx={{
                      maxWidth: 520,
                      mx: "auto",
                      textAlign: "center",
                      borderRadius: 3,
                      border: "1px solid #bae6fd",
                      background: "linear-gradient(145deg, #f2fcff 0%, #e8f8ff 100%)",
                      p: 4
                    }}
                  >
                    <Box
                      sx={{
                        width: 58,
                        height: 58,
                        mx: "auto",
                        mb: 1.8,
                        borderRadius: "50%",
                        background: "#cffafe",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#0f766e"
                      }}
                    >
                      <AccountTreeRoundedIcon sx={{ fontSize: 30 }} />
                    </Box>

                    <Typography variant="h6" sx={{ color: "#0f766e", fontWeight: 700, mb: 0.7 }}>
                      No projects found
                    </Typography>

                    <Typography sx={{ color: "#155e75", fontSize: 14, mb: 1.8 }}>
                      Create a project and assign it to a user to begin tracking work by project.
                    </Typography>

                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.7,
                        px: 1.4,
                        py: 0.6,
                        borderRadius: 999,
                        background: "#cffafe",
                        color: "#0f766e",
                        fontSize: 12,
                        fontWeight: 700
                      }}
                    >
                      <AutoAwesomeRoundedIcon sx={{ fontSize: 14 }} />
                      Ready for your first project
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && projects.length > rowsPerPage && (
        <Box
          mt={2}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={1}
        >
          <Button
            size="small"
            startIcon={<ChevronLeftIcon />}
            disabled={page === 1}
            onClick={() => setPage((prev) => prev - 1)}
          >
            Prev
          </Button>

          <Typography fontSize={13} color="#64748b">
            Page {page} of {totalPages}
          </Typography>

          <Button
            size="small"
            endIcon={<ChevronRightIcon />}
            disabled={page >= totalPages}
            onClick={() => setPage((prev) => prev + 1)}
          >
            Next
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default ProjectTable;
